<script setup>
import { computed } from 'vue';
import NutrientChart from './NutrientChart.vue';
import CollapsibleSection from './CollapsibleSection.vue';
import { rangeText } from '../utils/format.js';
import { hasChartData } from '../utils/dataAvailability.js';
import { nutrientChartSpecs } from '../utils/tableColumnSpecs.js';
import { sampleSeriesFor, axisRangeFor, lowOptHighBands, CEC_TEXTURE_BANDS } from '../utils/chartBands.js';

const props = defineProps({
	samples: { type: Array, required: true },
	optimumRanges: { type: Object, required: true },
	selectedGrower: { type: String, required: true },
	collapsed: { type: Boolean, required: true }
});

defineEmits(['update:collapsed']);

function buildChart(title, subtitle, get, range, unit, bandLabels) {
	const { labels, data, colors } = sampleSeriesFor(props.samples, get);
	const { axisMin, axisMax } = axisRangeFor(data, range || {});
	const bands = lowOptHighBands(range || {}, axisMin, axisMax, bandLabels);
	return { title, subtitle, labels, data, colors, axisMin, axisMax, bands, unit };
}

const phChart = computed(() => {
	if (!hasChartData(props.samples, s => s.ph)) return null;
	const range = props.optimumRanges.ph || {};
	return buildChart('Soil pH', 'Acid / Optimum / Alkaline — ' + rangeText(range), s => s.ph, range, '', { low: 'Acid', opt: 'Optimum', high: 'Alkaline' });
});

const nutrientCharts = computed(() => {
	return nutrientChartSpecs(props.optimumRanges)
		.filter(spec => hasChartData(props.samples, spec.get))
		.map(spec => buildChart(spec.title, 'Optimum: ' + rangeText(spec.range || {}) + (spec.unit ? ' ' + spec.unit : ''), spec.get, spec.range, spec.unit));
});

const cecChart = computed(() => {
	if (!hasChartData(props.samples, s => s.cec_meq)) return null;
	const { labels, data, colors } = sampleSeriesFor(props.samples, s => s.cec_meq);
	const numericData = data.filter(v => v != null);
	const dataMax = numericData.length ? Math.max(...numericData) : 40;
	const axisMax = Math.max(40, dataMax * 1.1);
	const axisMin = 0;
	const bands = CEC_TEXTURE_BANDS.map(band => ({
		yMin: band.min, yMax: Math.min(band.max, axisMax), color: band.color, label: band.label, labelColor: '#123c22'
	}));
	return {
		title: 'CEC / Estimated Texture',
		subtitle: 'Reference: Andersons general CEC-to-texture guide (not report-specific)',
		labels, data, colors, axisMin, axisMax, bands, unit: 'meq/100g'
	};
});

const allCharts = computed(() => {
	const charts = [];
	if (phChart.value) charts.push(phChart.value);
	charts.push(...nutrientCharts.value);
	if (cecChart.value) charts.push(cecChart.value);
	return charts;
});
</script>

<template>
	<CollapsibleSection
		title="Nutrient Charts"
		:visible="allCharts.length > 0"
		:model-value="collapsed"
		@update:model-value="$emit('update:collapsed', $event)"
	>
		<div class="tnt-chart-hint" style="display: block">Each chart labels its own bands directly — the words change per chart (Low/Optimum/High for nutrients, Acid/Optimum/Alkaline for pH, soil-texture classes for CEC), so a single shared legend would've been misleading.</div>
		<div class="tnt-chart-grid">
			<NutrientChart
				v-for="chart in allCharts"
				:key="selectedGrower + '::' + chart.title"
				v-bind="chart"
			/>
		</div>
	</CollapsibleSection>
</template>
